import React from 'react';
import { School, Users, GraduationCap, Heart, BookOpen, ShieldCheck, Activity, TrendingUp, AlertTriangle, CheckCircle2, Clock, Server, RefreshCw, Tag } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageLayout } from '../../shared/PageLayout';
import { StatCard } from '../../shared/StatCard';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { QualityRing, ProgressBar, containerVariants, itemVariants } from './dashboard/DashboardCharts';
import { useDashboardData } from './dashboard/useDashboardData';
export function DashboardPage({
  onNavigate
}: {
  onNavigate?: (view: string) => void;
}) {
  const {
    stats,
    contentStats,
    pipelineLoading,
    strictModeOn,
    handleRefreshPipeline,
    passCount,
    warnCount,
    failCount,
    avgScore,
    totalValidated,
    systemHealth,
    recentActivity
  } = useDashboardData();
  return <PageLayout title="Dashboard" subtitle="Overzicht van het Meester djay platform" actions={<ConnectionBadge status="live" data-id="element-4945" />} data-id="element-4944">
      {/* Section 1: Platform Overview */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6" data-id="element-4946">
        <motion.div variants={itemVariants} data-id="element-4947">
          <StatCard label="Leerlingen" value={stats.totalLearners} icon={<Users className="w-4 h-4 text-blue-500" data-id="element-4949" />} data-id="element-4948" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4950">
          <StatCard label="Docenten" value={stats.totalTeachers} icon={<GraduationCap className="w-4 h-4 text-indigo-500" data-id="element-4952" />} data-id="element-4951" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4953">
          <StatCard label="Ouders" value={stats.totalParents} icon={<Heart className="w-4 h-4 text-rose-500" data-id="element-4955" />} data-id="element-4954" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4956">
          <StatCard label="Scholen" value={stats.totalSchools} icon={<School className="w-4 h-4 text-purple-500" data-id="element-4958" />} data-id="element-4957" />
        </motion.div>
      </motion.div>

      {/* Section 2: Content & Quality */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5 mb-6" data-id="element-4959">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-4960">
          Content & Kwaliteit
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4961">
          {/* Left: Content Stats */}
          <div className="space-y-4" data-id="element-4962">
            <div className="flex items-center justify-between" data-id="element-4963">
              <div className="flex items-center gap-2" data-id="element-4964">
                <BookOpen className="w-4 h-4 text-blue-500" data-id="element-4965" />
                <span className="text-sm font-medium text-gray-700" data-id="element-4966">
                  Vragenbank
                </span>
              </div>
              <span className="text-lg font-bold text-gray-900" data-id="element-4967">
                {contentStats.totalQuestions.toLocaleString('nl')}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-4968">
              <div className="flex items-center gap-2" data-id="element-4969">
                <Tag className="w-4 h-4 text-purple-500" data-id="element-4970" />
                <span className="text-sm font-medium text-gray-700" data-id="element-4971">
                  Families
                </span>
              </div>
              <span className="text-sm text-gray-600" data-id="element-4972">
                {contentStats.totalFamilies} / {contentStats.taxonomyFamilies}
              </span>
            </div>
            <ProgressBar value={contentStats.coveragePct} color={contentStats.coveragePct >= 80 ? 'bg-emerald-500' : contentStats.coveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label="Seed dekking" data-id="element-4973" />
            <ProgressBar value={contentStats.visualCoveragePct} color={contentStats.visualCoveragePct >= 80 ? 'bg-emerald-500' : contentStats.visualCoveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label={`Visuele componenten (${contentStats.usedVisualsCount}/${contentStats.registeredVisuals})`} data-id="element-4974" />
          </div>

          {/* Right: Quality & Validation */}
          <div className="space-y-4" data-id="element-4975">
            <div className="flex items-center justify-between" data-id="element-4976">
              <div className="flex items-center gap-3" data-id="element-4977">
                {avgScore !== null ? <QualityRing score={avgScore} data-id="element-4978" /> : <div className="w-12 h-12 rounded-full bg-gray-100 animate-pulse" data-id="element-4979" />}
                <div data-id="element-4980">
                  <div className="text-sm font-medium text-gray-700" data-id="element-4981">
                    Kwaliteitsscore
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-4982">
                    alle {contentStats.totalQuestions.toLocaleString('nl')}{' '}
                    vragen
                  </div>
                </div>
              </div>
              <button onClick={handleRefreshPipeline} disabled={pipelineLoading} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40" title="Validatie vernieuwen" data-id="element-4983">
                <RefreshCw className={`w-4 h-4 ${pipelineLoading ? 'animate-spin' : ''}`} data-id="element-4984" />
              </button>
            </div>

            {pipelineLoading ? <div className="flex gap-4" data-id="element-4985">
                {['Geslaagd', 'Warnings', 'Fouten'].map(l => <div key={l} className="flex-1 bg-gray-50 rounded-lg p-2 animate-pulse" data-id="element-4986">
                    <div className="h-5 bg-gray-200 rounded mb-1" data-id="element-4987" />
                    <div className="h-3 bg-gray-100 rounded w-2/3" data-id="element-4988" />
                  </div>)}
              </div> : passCount !== null ? <>
                <div className="flex gap-3" data-id="element-4989">
                  <div className="flex-1 bg-emerald-50 rounded-lg p-2.5 text-center" data-id="element-4990">
                    <div className="text-lg font-bold text-emerald-700" data-id="element-4991">
                      {passCount}
                    </div>
                    <div className="text-xs text-emerald-600" data-id="element-4992">Geslaagd</div>
                  </div>
                  <div className="flex-1 bg-amber-50 rounded-lg p-2.5 text-center" data-id="element-4993">
                    <div className="text-lg font-bold text-amber-700" data-id="element-4994">
                      {warnCount}
                    </div>
                    <div className="text-xs text-amber-600" data-id="element-4995">Warnings</div>
                  </div>
                  <div className="flex-1 bg-red-50 rounded-lg p-2.5 text-center" data-id="element-4996">
                    <div className="text-lg font-bold text-red-700" data-id="element-4997">
                      {failCount}
                    </div>
                    <div className="text-xs text-red-600" data-id="element-4998">Fouten</div>
                  </div>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-4999">
                  <div className="h-full bg-emerald-500" style={{
                width: `${totalValidated > 0 ? (passCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-5000" />
                  <div className="h-full bg-amber-400" style={{
                width: `${totalValidated > 0 ? (warnCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-5001" />
                  <div className="h-full bg-red-500" style={{
                width: `${totalValidated > 0 ? (failCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-5002" />
                </div>
              </> : <div className="text-sm text-gray-400 text-center py-4" data-id="element-5003">
                Validatie wordt geladen...
              </div>}
          </div>
        </div>
      </motion.div>

      {/* Section 3: Activity & System Health */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6" data-id="element-5004">
        {/* Recent Activity */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-5005">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-5006">
            <Clock className="w-4 h-4" data-id="element-5007" /> Recente Activiteit
          </h3>
          <div className="space-y-3" data-id="element-5008">
            {recentActivity.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-5009">
                <div className={`mt-0.5 flex-shrink-0 ${item.type === 'success' ? 'text-emerald-500' : item.type === 'warning' ? 'text-amber-500' : 'text-blue-500'}`} data-id="element-5010">
                  {item.type === 'success' ? <CheckCircle2 className="w-4 h-4" data-id="element-5011" /> : item.type === 'warning' ? <AlertTriangle className="w-4 h-4" data-id="element-5012" /> : <Activity className="w-4 h-4" data-id="element-5013" />}
                </div>
                <div className="min-w-0 flex-1" data-id="element-5014">
                  <p className="text-sm text-gray-700 leading-snug" data-id="element-5015">
                    {item.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 font-mono" data-id="element-5016">
                    {item.time}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* System Health */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-5017">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-5018">
            <Server className="w-4 h-4" data-id="element-5019" /> Systeemgezondheid
          </h3>
          <div className="space-y-4" data-id="element-5020">
            <div className="flex items-center justify-between" data-id="element-5021">
              <span className="text-sm text-gray-600" data-id="element-5022">Strict Mode</span>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${strictModeOn ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-5023">
                {strictModeOn ? 'AAN' : 'UIT'}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-5024">
              <span className="text-sm text-gray-600" data-id="element-5025">Gem. responstijd</span>
              <span className="text-sm font-semibold text-gray-800" data-id="element-5026">
                {systemHealth.responseTime}ms
              </span>
            </div>
            <div data-id="element-5027">
              <div className="flex items-center justify-between mb-1.5" data-id="element-5028">
                <span className="text-sm text-gray-600" data-id="element-5029">Opslag</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-5030">
                  {systemHealth.storageMb} / 5 MB
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-5031">
                <div className="h-full bg-violet-500 rounded-full" style={{
                width: `${systemHealth.storagePct}%`
              }} data-id="element-5032" />
              </div>
            </div>
            <div data-id="element-5033">
              <div className="flex items-center justify-between mb-1.5" data-id="element-5034">
                <span className="text-sm text-gray-600" data-id="element-5035">Actief deze week</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-5036">
                  {stats.activeThisWeek} leerlingen
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-5037">
                <div className="h-full bg-blue-500 rounded-full" style={{
                width: `${Math.min(100, stats.activeThisWeek / Math.max(stats.totalLearners, 1) * 100)}%`
              }} data-id="element-5038" />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Section 4: Quick Insights */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-id="element-5039">
        {/* Subscriptions */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-5040">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-5041">
            Abonnementen
          </h3>
          <div className="space-y-2.5" data-id="element-5042">
            {stats.planDistribution.map(item => <div key={item.label} data-id="element-5043">
                <div className="flex justify-between text-xs mb-1" data-id="element-5044">
                  <span className="text-gray-600" data-id="element-5045">{item.label}</span>
                  <span className="font-semibold text-gray-800" data-id="element-5046">
                    {item.count}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-5047">
                  <div className={`h-full rounded-full ${item.color}`} style={{
                width: `${item.pct}%`
              }} data-id="element-5048" />
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Top Learners */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-5049">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-5050">
            <TrendingUp className="w-3.5 h-3.5" data-id="element-5051" /> Top Leerlingen
          </h3>
          {stats.topLearners.length > 0 ? <div className="space-y-2.5" data-id="element-5052">
              {stats.topLearners.map((item, i) => <div key={i} className="flex items-center gap-2.5" data-id="element-5053">
                  <div className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center flex-shrink-0" data-id="element-5054">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-5055">
                    <p className="text-xs font-medium text-gray-800 truncate" data-id="element-5056">
                      {item.name}
                    </p>
                  </div>
                  <span className="text-xs font-bold text-violet-600" data-id="element-5057">
                    {item.xp.toLocaleString('nl-NL')} XP
                  </span>
                </div>)}
            </div> : <p className="text-xs text-gray-400" data-id="element-5058">Geen leerlingen gevonden</p>}
        </motion.div>

        {/* Calibration Progress */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-5059">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-5060">
            <ShieldCheck className="w-3.5 h-3.5" data-id="element-5061" /> Kalibratie
          </h3>
          <div className="space-y-3" data-id="element-5062">
            <div className="flex items-end justify-between" data-id="element-5063">
              <span className="text-2xl font-bold text-gray-900" data-id="element-5064">
                {contentStats.calibration.goldStandardPct}%
              </span>
              <span className="text-xs text-gray-500" data-id="element-5065">
                {contentStats.calibration.familiesAtGoldStandard} /{' '}
                {contentStats.calibration.totalFamilies} goud
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-5066">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${contentStats.calibration.goldStandardPct}%`
            }} transition={{
              duration: 1,
              delay: 0.3
            }} className={`h-full rounded-full ${contentStats.calibration.goldStandardPct >= 80 ? 'bg-emerald-500' : contentStats.calibration.goldStandardPct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} data-id="element-5067" />
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs" data-id="element-5068">
              <div className="flex justify-between" data-id="element-5069">
                <span className="text-gray-500" data-id="element-5070">citoDifficulty</span>
                <span className="font-semibold text-gray-700" data-id="element-5071">
                  {contentStats.calibration.citoDifficultyCoveragePct}%
                </span>
              </div>
              <div className="flex justify-between" data-id="element-5072">
                <span className="text-gray-500" data-id="element-5073">solutionSteps</span>
                <span className="font-semibold text-gray-700" data-id="element-5074">
                  {contentStats.calibration.solutionStepsCoveragePct}%
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </PageLayout>;
}